/**
 * E5.4's tail patch (ARCHITECTURE §5.17): what changed between two flora lists, when it is exactly a forest join or leave.
 * `stepForestHeld` removes a leaving cell's trees by filter and appends a joining cell's at the tail, so the new list is
 * the old with whole cells' runs taken out of the forest block and whole cells' runs of new objects added at the end;
 * every other entry the same object in the same order. The four flora tables (core/surfaceWalk.ts, core/hits.ts,
 * core/foliage.ts) patch themselves for that change and build in full for any other.
 *
 * Lane A's read: the prefix and every kept entry must be the same object in the same position; joined runs come in the
 * new list's order; a cell is wholly kept, wholly gone or wholly new (a tree felled in place — an `alive` flip, a new
 * object — leaves its cell partly kept and partly new, so it is not a splice). Anything else is null: a full build.
 */
import type { FloraState } from '../types';
import { FOREST, forestIdCell } from './forest';

export interface FloraSplice {
  /** the old list's indices that left, ascending */
  removed: number[];
  /** where the joined run starts in the new list (it runs to the end) */
  addStart: number;
}

const forestCellKey = (f: FloraState): number | null => {
  if (f.id < FOREST.idBase) return null;
  const c = forestIdCell(f.id);
  return c === null ? null : f.planetIndex * 16777216 + c;   // (2^24: forestHeld.ts forestKey's spacing)
};

let last: { old: readonly FloraState[]; neu: readonly FloraState[]; value: FloraSplice | null } | null = null;
let off = false;

/** For the tests (lane A's read): run `fn` with no table patched for a join or leave — every such change built in full. */
export function withoutTailPatch<T>(fn: () => T): T {
  const was = off;
  off = true;
  try { return fn(); } finally { off = was; }
}

/** The splice from `old` to `neu`, or null when the change is anything but forest cells leaving and joining. Pure (memoised on the pair). */
export function floraSplice(old: readonly FloraState[], neu: readonly FloraState[]): FloraSplice | null {
  if (off) return null;
  if (last && last.old === old && last.neu === neu) return last.value;
  const value = splice(old, neu);
  last = { old, neu, value };
  return value;
}

function splice(old: readonly FloraState[], neu: readonly FloraState[]): FloraSplice | null {
  if (old === neu) return null;
  const removed: number[] = [];
  const keptCells = new Set<number>(), goneCells = new Set<number>();
  let j = 0;
  for (let i = 0; i < old.length; i++) {
    const f = old[i];
    if (j < neu.length && f === neu[j]) {
      const k = forestCellKey(f);
      if (k !== null) keptCells.add(k);
      j++;
      continue;
    }
    // a gap in the kept order: only a forest tree may leave
    const k = forestCellKey(f);
    if (k === null) return null;
    removed.push(i);
    goneCells.add(k);
  }
  const addStart = j;
  if (!removed.length && addStart === neu.length) return null;
  // a cell is wholly kept or wholly gone
  for (const k of goneCells) if (keptCells.has(k)) return null;
  // the joined run: forest trees, new objects, each cell's trees together and the cell not one still kept
  const goneObjects = new Set(removed.map(i => old[i]));
  const seen = new Set<number>();
  let prev: number | null = null;
  for (let t = addStart; t < neu.length; t++) {
    const f = neu[t], k = forestCellKey(f);
    if (k === null || goneObjects.has(f) || keptCells.has(k)) return null;
    if (k !== prev) { if (seen.has(k)) return null; seen.add(k); prev = k; }
  }
  return { removed, addStart };
}
